"use client";

import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Newspaper, Brain, Filter, Sparkles } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrosoft,
  faXTwitter,
  faYahoo,
  faReddit,
} from "@fortawesome/free-brands-svg-icons";
import { useAuth } from "@/contexts/AuthContext";
import { getViewModeChrome } from "../view-mode-chrome";

// Brand icon wrappers (Font Awesome)
// These render inline SVGs and inherit color via currentColor (Tailwind text-* classes)
const XIcon = (props: any) => (
  <FontAwesomeIcon icon={faXTwitter} className={props.className} />
);
const MicrosoftIcon = (props: any) => (
  <FontAwesomeIcon icon={faMicrosoft} className={props.className} />
);
const YahooIcon = (props: any) => (
  <FontAwesomeIcon icon={faYahoo} className={props.className} />
);
const RedditIcon = (props: any) => (
  <FontAwesomeIcon icon={faReddit} className={props.className} />
);
// Minimal custom Bloomberg "B" glyph approximating the brand in monochrome
const BloombergIcon = (props: any) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={props.className}
  >
    <path
      fill="currentColor"
      d="M7 3h6a4 4 0 0 1 0 8H7V3Zm0 10h6a4 4 0 1 1 0 8H7v-8Z"
    />
  </svg>
);

// Financial sources that get randomized
const financialSources = [
  { name: "Bloomberg", icon: BloombergIcon },
  { name: "MSN", icon: MicrosoftIcon },
  { name: "Yahoo Finance", icon: YahooIcon },
];

export function UnifiedSearchLoading({ viewMode }: { viewMode?: string } = {}) {
  const chrome = getViewModeChrome(viewMode);
  const [currentStage, setCurrentStage] = useState(0);
  const { user } = useAuth();

  // Randomize financial sources order on component mount
  const randomizedFinancialSources = useMemo(() => {
    return [...financialSources].sort(() => Math.random() - 0.5);
  }, []);

  // Build the complete stages array with randomized financial sources
  const stages = useMemo(() => {
    // Use shorter durations if user is not authenticated
    const durationMultiplier = user ? 1.2 : 0.6;

    const baseStages: any[] = [
      {
        id: 1,
        text: "Initializing search...",
        duration: 2000 * durationMultiplier,
        icon: Search,
        iconColor: "text-muted-foreground",
        bgColor: "bg-muted/50",
        animationType: "pulse",
      },
      {
        id: 2,
        text: "Scanning Washington Post...",
        duration: 2500 * durationMultiplier,
        icon: Newspaper,
        iconColor: "text-muted-foreground",
        bgColor: "bg-muted/55",
        animationType: "bounce",
      },
    ];

    // Add randomized financial sources
    const animationTypes = ["pulse", "pulse", "pulse"];
    const semanticColors = [
      "text-muted-foreground",
      "text-muted-foreground",
      "text-muted-foreground",
    ];
    const semanticBgColors = ["bg-muted/60", "bg-muted/65", "bg-muted/70"];
    randomizedFinancialSources.forEach((source, index) => {
      baseStages.push({
        id: baseStages.length + 1,
        text: `Scanning ${source.name}...`,
        duration: 2000 * durationMultiplier,
        icon: source.icon,
        iconColor: semanticColors[index % semanticColors.length],
        bgColor: semanticBgColors[index % semanticBgColors.length],
        animationType: animationTypes[index % animationTypes.length],
      });
    });

    // Add remaining stages
    const remainingStages: any[] = [
      {
        id: baseStages.length + 1,
        text: "Scanning Reddit...",
        duration: 2000 * durationMultiplier,
        icon: RedditIcon,
        iconColor: "text-muted-foreground",
        bgColor: "bg-muted/75",
        animationType: "pulse",
      },
      {
        id: baseStages.length + 2,
        text: "Scanning Twitter...",
        duration: 1500 * durationMultiplier,
        icon: XIcon,
        iconColor: "text-muted-foreground",
        bgColor: "bg-muted/80",
        animationType: "bounce",
      },
      {
        id: baseStages.length + 3,
        text: "Generating insights...",
        duration: 2500 * durationMultiplier,
        icon: Brain,
        iconColor: "text-foreground/90",
        bgColor: "bg-muted/85",
        animationType: "pulse",
      },
      {
        id: baseStages.length + 4,
        text: "Cleaning for relevance...",
        duration: 2000 * durationMultiplier,
        icon: Filter,
        iconColor: "text-foreground/90",
        bgColor: "bg-muted/90",
        animationType: "pulse",
      },
      {
        id: baseStages.length + 5,
        text: "Generating answer...",
        duration: 3500 * durationMultiplier, // Remaining time to reach ~18 seconds
        icon: Sparkles,
        iconColor: "text-foreground",
        bgColor: "bg-muted/95",
        animationType: "pulse",
      },
    ];

    return [...baseStages, ...remainingStages];
  }, [randomizedFinancialSources, user]);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    let totalDelay = 0;

    stages.forEach((stage, index) => {
      const timer = setTimeout(() => {
        setCurrentStage(index);
      }, totalDelay);

      totalDelay += stage.duration;
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  const currentStageData = stages[currentStage] || stages[0];
  const Icon: any = currentStageData.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        {/* Progress bar */}
        <div className="absolute top-0 left-0 h-[1px] bg-gradient-to-r from-transparent via-border to-transparent">
          <motion.div
            className="h-full bg-gradient-to-r from-foreground/0 via-foreground/80 to-foreground/0"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 18, ease: "linear" }}
          />
        </div>

        <div className="px-3 py-2 flex items-center gap-3 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStage}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2.5"
            >
              {/* Animated icon with stage-specific styling */}
              <motion.div
                animate={
                  currentStageData.animationType === "pulse"
                    ? { scale: [1, 1.1, 1], opacity: [0.6, 1, 0.6] }
                    : currentStageData.animationType === "bounce"
                    ? { y: [0, -3, 0], scale: [1, 1.05, 1] }
                    : currentStageData.animationType === "rotate"
                    ? { rotate: [0, 360], scale: [1, 1.05, 1] }
                    : currentStageData.animationType === "scale"
                    ? { scale: [1, 1.2, 1], opacity: [0.7, 1, 0.7] }
                    : currentStageData.animationType === "wiggle"
                    ? { rotate: [-2, 2, -2, 2, 0], scale: [1, 1.05, 1] }
                    : { scale: [1, 1.1, 1], opacity: [0.6, 1, 0.6] }
                }
                transition={{
                  duration:
                    currentStageData.animationType === "rotate" ? 2 : 1.5,
                  repeat: Infinity,
                  ease:
                    currentStageData.animationType === "bounce"
                      ? "easeOut"
                      : "easeInOut",
                }}
                className={`w-7 h-7 rounded ${currentStageData.bgColor} flex items-center justify-center`}
              >
                <Icon className={`w-4 h-4 ${currentStageData.iconColor}`} />
              </motion.div>

              {/* Stage text */}
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {currentStageData.text}
              </span>

              {/* Stage indicator */}
              <div className="ml-auto flex items-center gap-1">
                {stages.map((_, index) => (
                  <div
                    key={index}
                    className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                      index <= currentStage
                        ? "bg-foreground/60"
                        : "bg-muted-foreground/40"
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
